import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit'
import { setPlatformSettings } from '@wellesley/host-remote/api/actions'
import {
  PlatformSettings,
  UploadType,
  VideoPolicy,
  AudioPolicy,
  DocPolicy,
  ImagePolicy,
} from '@wellesley/openapi'
import { ExtensionsMap } from '../../types'
import {
  adminOnlyUserSettingsKeysMap,
  platformSettingsKeysMap,
} from '../../keys'

export const mapExtensions = (
  extensions: string[] | { value?: string }[] | undefined,
) => {
  return (
    extensions?.map((extension) =>
      typeof extension === 'string' ? extension : extension.value || '',
    ) || []
  )
}

export type PlatformSettingsState = {
  data: PlatformSettings | null
  isPending: boolean
  error?: unknown
}

const initialState: PlatformSettingsState = {
  data: null,
  isPending: false,
}

const {
  posts,
  uploadsPolicyDoc,
  uploadsPolicyAudio,
  uploadsPolicyImage,
  uploadsPolicyVideo,
  registration,
  captcha,
  instanceAdmin,
  branding,
  translation,
  domainBlocks,
  access,
} = platformSettingsKeysMap

const {
  captcha: adminCaptcha,
  language: adminLanguage,
  translation: adminTranslation,
  link_service: adminLinkService,
} = adminOnlyUserSettingsKeysMap

// TODO: discuss all optional values with backend

const selectSettings = (state: PlatformSettingsState) => state.data

const selectPostConfig = createSelector(selectSettings, (settings) => ({
  ...settings?.[posts],
  default_markdown: settings?.[posts]?.default_markdown || false,
  max_characters: settings?.[posts]?.max_characters || 0,
  max_media_attachments: settings?.[posts]?.max_media_attachments || 0,
  max_pins: settings?.[posts]?.max_pins || 0,
}))

const selectImagePolicy = createSelector(selectSettings, (settings) => {
  return {
    ...settings?.[uploadsPolicyImage],
    maxSize: settings?.[uploadsPolicyImage]?.maxSize || 0,
    maxMatrix: settings?.[uploadsPolicyImage]?.maxMatrix || 0,
    maxResolution: settings?.[uploadsPolicyImage]?.maxResolution || 0,
  } as Required<ImagePolicy>
})

const selectImageExtensions = createSelector(selectImagePolicy, (policy) =>
  mapExtensions(policy.extensions),
)

const selectVideoPolicy = createSelector(
  selectSettings,
  (settings) =>
    ({
      ...settings?.[uploadsPolicyVideo],
      maxSize: settings?.[uploadsPolicyVideo]?.maxSize || 0,
      maxMatrix: settings?.[uploadsPolicyVideo]?.maxMatrix || 0,
      maxDuration: settings?.[uploadsPolicyVideo]?.maxDuration || 0,
      maxResolution: settings?.[uploadsPolicyVideo]?.maxResolution || 0,
    }) as Required<VideoPolicy>,
)

const selectVideoExtensions = createSelector(selectVideoPolicy, (policy) =>
  mapExtensions(policy.extensions),
)

const selectAudioPolicy = createSelector(
  selectSettings,
  (settings) =>
    ({
      ...settings?.[uploadsPolicyAudio],
      maxSize: settings?.[uploadsPolicyAudio]?.maxSize,
      keepOriginal: settings?.[uploadsPolicyAudio]?.keepOriginal || 0,
    }) as Required<AudioPolicy>,
)

const selectDocPolicy = createSelector(
  selectSettings,
  (settings) =>
    ({
      ...settings?.[uploadsPolicyDoc],
      maxSize: settings?.[uploadsPolicyDoc]?.maxSize || 0,
    }) as Required<DocPolicy>,
)

const selectSupportedExtensions = createSelector(
  [selectImagePolicy, selectVideoPolicy, selectAudioPolicy, selectDocPolicy],
  (img, video, audio, doc) =>
    ({
      IMAGE: mapExtensions(img?.extensions),
      VIDEO: mapExtensions(video?.extensions),
      AUDIO: mapExtensions(audio?.extensions),
      DOCUMENT: mapExtensions(doc?.extensions),
    }) as ExtensionsMap,
)

const selectFullExtensionsList = createSelector(
  selectSupportedExtensions,
  (ext) =>
    Object.keys(ext).reduce((acc, value) => {
      const subList = ext[value as UploadType] || []

      return [...acc, ...subList]
    }, [] as string[]),
)

const selectInvitesEnabled = createSelector(
  selectSettings,
  (settings) => settings?.[registration]?.invites,
)

export const platformSettingsSlice = createSlice({
  name: 'platformSettings',
  initialState,
  reducers: {
    setError: (state, action: PayloadAction<unknown>) => {
      state.error = action.payload
    },
    setPending: (state, action: PayloadAction<boolean>) => {
      state.isPending = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setPlatformSettings, (state, { payload }) => {
      state.data = {
        ...state.data,
        ...payload,
      }
    })
  },
  selectors: {
    selectSettings,
    selectPostConfig,
    selectDocPolicy,
    selectImagePolicy,
    selectVideoPolicy,
    selectAudioPolicy,
    selectInvitesEnabled,
    selectImageExtensions,
    selectVideoExtensions,
    selectSupportedExtensions,
    selectFullExtensionsList,
    selectRegistration: (state) => state.data?.[registration],
    selectLanguageDetection: (state) => state.data?.[adminLanguage],
    selectLanguageTranslation: (state) => state.data?.[adminTranslation],
    selectCaptcha: (state) => state.data?.[captcha],
    selectAdminCaptcha: (state) => state.data?.[adminCaptcha],
    selectPlatformTranslation: (state) => state.data?.[translation],
    selectAccess: (state) => state.data?.[access],
    selectBranding: (state) => state.data?.[branding],
    selectLinkService: (state) => state.data?.[adminLinkService],
    selectInstanceAdmin: (state) => state.data?.[instanceAdmin],
    selectLegal: (state) => state.data?.[platformSettingsKeysMap.legal],
    selectDomainBlocks: (state) => state.data?.[domainBlocks],
    selectContacts: (state) => state.data?.[platformSettingsKeysMap.contacts],
  },
})
